import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { getSaleById } from "@/api/sale/getSaleById";
import type { Sale, SaleAddress } from "@/api/sale/_models/Sale";

interface SaleDetailModalProps {
  saleId: string | null;
  onClose: () => void;
}

const formatAddress = (a: SaleAddress): string => {
  return [a.line1, a.line2, a.city, a.state, a.postcode, a.country]
    .filter(Boolean)
    .join(", ");
};

const detailRows = (sale: Sale): Array<{ label: string; value: string }> => {
  return [
    { label: "ID", value: sale.id },
    { label: "Customer", value: sale.customer },
    { label: "Status", value: sale.status },
    { label: "Type", value: sale.type },
    { label: "Order Date", value: sale.saleOrderDate },
    { label: "Ship By", value: sale.shipBy },
    { label: "Tax Rule", value: sale.taxRule },
    { label: "Terms", value: sale.terms },
    { label: "Price Tier", value: sale.priceTier },
    { label: "Location", value: sale.location },
    { label: "Carrier", value: sale.carrier ?? "—" },
    { label: "Sales Rep", value: sale.salesRepresentative ?? "—" },
    { label: "Currency", value: `${sale.baseCurrency} (rate ${sale.currencyRate})` },
    { label: "Customer Ref", value: sale.customerReference || "—" },
    { label: "Billing", value: formatAddress(sale.billingAddress) },
    { label: "Shipping", value: formatAddress(sale.shippingAddress) },
    { label: "Last Modified", value: sale.lastModifiedOn },
  ];
};

export function SaleDetailModal(props: SaleDetailModalProps) {
  const { saleId, onClose } = props;
  const query = getSaleById.useQuery(saleId ?? skipToken);
  const sale = query.data;

  return (
    <Dialog
      open={saleId !== null}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Sale detail</DialogTitle>
      <DialogContent dividers>
        {query.isFetching && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {query.isError && (
          <Alert severity="error">
            Failed to load sale: {JSON.stringify(query.error)}
          </Alert>
        )}
        {!query.isFetching && sale && (
          <Table size="small">
            <TableBody>
              {detailRows(sale).map((row) => (
                <TableRow key={row.label}>
                  <TableCell
                    component="th"
                    sx={{ fontWeight: 600, width: 160 }}
                  >
                    {row.label}
                  </TableCell>
                  <TableCell>{row.value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
